import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CachedRoundedIcon from '@mui/icons-material/CachedRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import apiFetch from '../api/apiFetch';
import { pageColors } from './HomePage';

const tablePaperSx = {
  p: { xs: 1.5, md: 1.75 },
  borderRadius: 3,
  backgroundColor: '#fff',
  boxShadow: '0 12px 30px rgba(0,0,0,0.06)',
  border: '1px solid rgba(0,0,0,0.06)',
};

const headerPaperSx = {
  ...tablePaperSx,
  p: { xs: 1.5, md: 2 },
};

const dateWindowColumns = [
  { key: 'startDate', label: 'Start date', width: '36%' },
  { key: 'endDate', label: 'End date', width: '36%' },
  { key: 'size', label: 'Size', align: 'right', width: '28%' },
];

function parseSize(value) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function formatCount(value) {
  return parseSize(value).toLocaleString();
}

function formatDate(value, withTime = false) {
  if (!value) {
    return 'Unknown';
  }

  if (value === 'Not refreshed yet') {
    return value;
  }

  const normalizedValue = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
  const parsedDate = new Date(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    ...(withTime ? { timeStyle: 'short' } : {}),
  }).format(parsedDate);
}

function readSafeError(response, fallback) {
  return response.text().then((text) => text || fallback).catch(() => fallback);
}

function formatHttpError(response) {
  return `HTTP ${response.status}`;
}

function sortDateWindows(items, prefix) {
  return (Array.isArray(items) ? [...items] : [])
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
    .map((stat, index) => ({
      id: `${prefix}-${stat.startDate || 'unknown'}-${stat.endDate || 'unknown'}-${index}`,
      size: parseSize(stat.size),
      cells: [
        { key: `${prefix}-start-${index}`, columnKey: 'startDate', value: formatDate(stat.startDate), noWrap: true },
        { key: `${prefix}-end-${index}`, columnKey: 'endDate', value: formatDate(stat.endDate), noWrap: true },
        {
          key: `${prefix}-size-${index}`,
          columnKey: 'size',
          value: formatCount(stat.size),
          align: 'right',
          noWrap: true,
        },
      ],
    }));
}

function buildRefreshStatusMessage(responseText) {
  if (responseText === 'Cache refresh already running') {
    return {
      severity: 'info',
      text: responseText,
    };
  }

  if (responseText === 'Required caches are already populated; no refresh was started') {
    return {
      severity: 'info',
      text: `${responseText}. Last refresh time was not updated because no refresh ran.`,
    };
  }

  return {
    severity: 'success',
    text: responseText || 'Cache refresh request accepted.',
  };
}

function SectionTable({ title, description, columns, rows, emptyMessage }) {
  return (
    <Paper elevation={0} sx={tablePaperSx}>
      <Stack spacing={1.25}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>

        <TableContainer
          sx={{
            borderRadius: 3,
            border: '1px solid rgba(0,0,0,0.06)',
            overflowX: 'auto',
          }}
        >
          <Table
            size="small"
            sx={{ minWidth: 520, tableLayout: columns.some((column) => column.width) ? 'fixed' : 'auto' }}
          >
            <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align || 'left'}
                    sx={{ fontWeight: 700, whiteSpace: 'nowrap', width: column.width }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ py: 4, color: 'text.secondary' }}>
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      '&:last-child td, &:last-child th': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    {row.cells.map((cell) => (
                      <TableCell
                        key={cell.key}
                        align={cell.align || 'left'}
                        sx={{
                          whiteSpace: cell.noWrap ? 'nowrap' : 'normal',
                          width: columns.find((column) => column.key === cell.columnKey)?.width,
                        }}
                      >
                        {cell.value}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Paper>
  );
}

export default function CacheStatusPage({ actingTenant, connected, currentUser, setConnected, setError }) {
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [refreshPending, setRefreshPending] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [cacheStatus, setCacheStatus] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tenantName = actingTenant?.company || currentUser?.company || 'Unknown tenant';
  const tenantScopeLabel = actingTenant ? `Active tenant: ${tenantName}` : `Tenant: ${tenantName}`;
  const tenantScopeDescription = actingTenant
    ? 'This cache snapshot is scoped to the selected acting tenant context.'
    : 'This cache snapshot is scoped to the signed-in tenant account.';

  const fetchCacheStatus = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) {
        setReloading(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await apiFetch('/api/cache/status');

        if (!response.ok) {
          if (response.status === 401) {
            setConnected(false);
            setError('Your session is no longer authorized. Please sign in again.');
            setStatusMessage({
              severity: 'warning',
              text: 'Your session is no longer authorized. Please sign in again.',
            });
          } else {
            const message = `Failed to fetch cache status (${formatHttpError(response)}). Please try again.`;
            setError(message);
            setStatusMessage({ severity: 'error', text: message });
          }

          return;
        }

        const data = await response.json();
        setCacheStatus(data);
      } catch (error) {
        const message = 'Unable to fetch cache status. Please try again.';
        setError(message);
        setStatusMessage({ severity: 'error', text: message });
      } finally {
        setLoading(false);
        setReloading(false);
      }
    },
    [setConnected, setError]
  );

  useEffect(() => {
    fetchCacheStatus();
  }, [fetchCacheStatus]);

  const lastRefreshTime = cacheStatus?.lastRefreshTime ?? 'Not refreshed yet';
  const generalCaches = useMemo(
    () =>
      cacheStatus?.generalCaches &&
      typeof cacheStatus.generalCaches === 'object' &&
      !Array.isArray(cacheStatus.generalCaches)
        ? cacheStatus.generalCaches
        : {},
    [cacheStatus]
  );
  const articleStats = useMemo(
    () => (Array.isArray(cacheStatus?.articleStats) ? cacheStatus.articleStats : []),
    [cacheStatus]
  );
  const customerStats = useMemo(
    () => (Array.isArray(cacheStatus?.customerStats) ? cacheStatus.customerStats : []),
    [cacheStatus]
  );
  const orders = useMemo(() => (Array.isArray(cacheStatus?.orders) ? cacheStatus.orders : []), [cacheStatus]);
  const topSpenders = useMemo(
    () => (Array.isArray(cacheStatus?.topSpenders) ? cacheStatus.topSpenders : []),
    [cacheStatus]
  );
  const topSellers = useMemo(
    () => (Array.isArray(cacheStatus?.topSellers) ? cacheStatus.topSellers : []),
    [cacheStatus]
  );

  const generalCacheRows = useMemo(
    () =>
      Object.entries(generalCaches)
        .sort(([nameA, sizeA], [nameB, sizeB]) => parseSize(sizeB) - parseSize(sizeA) || nameA.localeCompare(nameB))
        .map(([name, size]) => ({
          id: name,
          size: parseSize(size),
          cells: [
            { key: `${name}-name`, value: name },
            { key: `${name}-size`, value: formatCount(size), align: 'right', noWrap: true },
          ],
        })),
    [generalCaches]
  );

  const sortedArticleStats = useMemo(() => sortDateWindows(articleStats, 'article'), [articleStats]);
  const sortedCustomerStats = useMemo(() => sortDateWindows(customerStats, 'customer'), [customerStats]);
  const sortedOrders = useMemo(() => sortDateWindows(orders, 'orders'), [orders]);
  const sortedTopSpenders = useMemo(() => sortDateWindows(topSpenders, 'top-spenders'), [topSpenders]);
  const sortedTopSellers = useMemo(() => sortDateWindows(topSellers, 'top-sellers'), [topSellers]);

  const renderedCacheGroups = useMemo(
    () => [
      { title: 'General caches', rows: generalCacheRows },
      { title: 'Article windows', rows: sortedArticleStats },
      { title: 'Customer windows', rows: sortedCustomerStats },
      { title: 'Order windows', rows: sortedOrders },
      { title: 'Top spender windows', rows: sortedTopSpenders },
      { title: 'Top seller windows', rows: sortedTopSellers },
    ],
    [generalCacheRows, sortedArticleStats, sortedCustomerStats, sortedOrders, sortedTopSpenders, sortedTopSellers]
  );

  const totalEntries = renderedCacheGroups.reduce(
    (sum, group) => sum + group.rows.reduce((groupSum, row) => groupSum + parseSize(row.size), 0),
    0
  );

  const totalCacheRows = renderedCacheGroups.reduce((sum, group) => sum + group.rows.length, 0);
  const metricCards = [
    {
      title: 'General caches',
      value: generalCacheRows.length.toLocaleString(),
      description: 'Shared cache buckets available now.',
      color: pageColors.summary,
      icon: StorageRoundedIcon,
    },
    {
      title: 'Article windows',
      value: sortedArticleStats.length.toLocaleString(),
      description: 'Cached article statistics date ranges.',
      color: pageColors.articles,
      icon: Inventory2RoundedIcon,
    },
    {
      title: 'Customer windows',
      value: sortedCustomerStats.length.toLocaleString(),
      description: 'Cached customer statistics date ranges.',
      color: pageColors.customerSales,
      icon: PeopleAltRoundedIcon,
    },
    {
      title: 'Order windows',
      value: sortedOrders.length.toLocaleString(),
      description: 'Cached order date ranges.',
      color: pageColors.orders,
      icon: ReceiptLongRoundedIcon,
    },
    {
      title: 'Top spender windows',
      value: sortedTopSpenders.length.toLocaleString(),
      description: 'Cached top spender ranges.',
      color: pageColors.statistics,
      icon: TrendingUpRoundedIcon,
    },
    {
      title: 'Top seller windows',
      value: sortedTopSellers.length.toLocaleString(),
      description: 'Cached top seller ranges.',
      color: pageColors.statistics,
      icon: TrendingUpRoundedIcon,
    },
    {
      title: 'Total entries',
      value: totalEntries.toLocaleString(),
      description: `${totalCacheRows.toLocaleString()} rendered cache rows.`,
      color: '#1e88e5',
      icon: CachedRoundedIcon,
    },
  ];

  const refreshDisabled = refreshPending || Boolean(cacheStatus?.isRefreshing);
  const statusCompany = cacheStatus?.company || tenantName;
  const populated = Boolean(cacheStatus?.populated);

  const handleRefresh = async () => {
    setRefreshPending(true);
    setStatusMessage(null);

    try {
      const response = await apiFetch('/api/cache/refresh', { method: 'POST' });

      if (!response.ok) {
        if (response.status === 401) {
          setConnected(false);
          setStatusMessage({
            severity: 'warning',
            text: 'Your session is no longer authorized. Please sign in again.',
          });
          setError('Your session is no longer authorized. Please sign in again.');
        } else {
          setStatusMessage({
            severity: 'error',
            text: `Failed to trigger cache refresh (${formatHttpError(response)}). Please try again.`,
          });
        }

        return;
      }

      const responseText = await readSafeError(response, response.statusText || String(response.status));
      setStatusMessage(buildRefreshStatusMessage(responseText));
      fetchCacheStatus({ silent: true });
    } catch (error) {
      setStatusMessage({ severity: 'error', text: 'Unable to trigger cache refresh. Please try again.' });
    } finally {
      setRefreshPending(false);
    }
  };

  useEffect(() => {
    if (!cacheStatus?.isRefreshing) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      fetchCacheStatus({ silent: true });
    }, 2000);

    return () => window.clearInterval(intervalId);
  }, [cacheStatus?.isRefreshing, fetchCacheStatus]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f7f9fb',
        py: { xs: 3, md: 4 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={2.25}>
          <Paper elevation={0} sx={headerPaperSx}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar sx={{ width: 48, height: 48, bgcolor: `${pageColors.summary}18`, color: pageColors.summary }}>
                  <StorageRoundedIcon fontSize="large" />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" sx={{ color: pageColors.summary, letterSpacing: 3 }}>
                    CACHE STATUS
                  </Typography>
                  <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                    Cache Status
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 0.25, maxWidth: 880 }}>
                    Monitor refresh state and cached date windows for the current tenant.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Paper>

          <Paper elevation={0} sx={tablePaperSx}>
            <Stack spacing={1.25}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.25} justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Actions and status
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Viewing cache data for <strong>{tenantName}</strong>. {tenantScopeDescription}
                  </Typography>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<RefreshRoundedIcon />}
                    disabled={refreshDisabled}
                    onClick={handleRefresh}
                    sx={{ minHeight: 38 }}
                  >
                    {refreshPending ? 'Triggering...' : cacheStatus?.isRefreshing ? 'Refreshing' : 'Refresh cache'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ReplayRoundedIcon />}
                    disabled={loading || reloading}
                    onClick={() => fetchCacheStatus({ silent: true })}
                    sx={{ minHeight: 38 }}
                  >
                    Reload status
                  </Button>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={populated ? 'Populated' : 'Not populated'}
                  color={populated ? 'success' : 'default'}
                />
                <Chip
                  size="small"
                  label={cacheStatus?.isRefreshing ? 'Refresh in progress' : 'Idle'}
                  color={cacheStatus?.isRefreshing ? 'warning' : 'default'}
                />
                <Chip size="small" label={`Last refresh: ${formatDate(lastRefreshTime, true)}`} variant="outlined" />
                <Chip size="small" label={tenantScopeLabel} color="primary" variant="outlined" />
                <Chip size="small" label={`Company: ${statusCompany}`} variant="outlined" />
                <Chip
                  size="small"
                  label={connected ? 'Connected to Fortnox' : 'Fortnox connection required'}
                  color={connected ? 'success' : 'default'}
                  variant="outlined"
                />
                {cacheStatus?.lastError && (
                  <Chip size="small" label={`Last error: ${cacheStatus.lastError}`} color="error" variant="outlined" />
                )}
              </Stack>

              {(loading || reloading || cacheStatus?.isRefreshing) && <LinearProgress />}
              {statusMessage && <Alert severity={statusMessage.severity}>{statusMessage.text}</Alert>}
              {cacheStatus?.lastError && <Alert severity="error">Last cache error: {cacheStatus.lastError}</Alert>}
            </Stack>
          </Paper>

          {loading ? (
            <Paper elevation={0} sx={tablePaperSx}>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'rgba(25,118,210,0.12)', color: '#1e88e5' }}>
                    <ScheduleRoundedIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Loading cache diagnostics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pulling the latest cache breakdown from the API.
                    </Typography>
                  </Box>
                </Stack>
                <LinearProgress />
              </Stack>
            </Paper>
          ) : !cacheStatus ? (
            <Paper elevation={0} sx={tablePaperSx}>
              <Alert severity="info">No cache status is available yet.</Alert>
            </Paper>
          ) : (
            <Stack spacing={{ xs: 3, md: 4 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'minmax(0, 1fr)',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(4, minmax(0, 1fr))',
                    xl: 'repeat(7, minmax(0, 1fr))',
                  },
                  gap: 1,
                  alignItems: 'stretch',
                }}
              >
                {metricCards.map((card) => {
                  const IconComponent = card.icon;

                  return (
                    <Paper
                      key={card.title}
                      elevation={0}
                      sx={{
                        p: 1,
                        borderRadius: 3,
                        backgroundColor: '#fff',
                        boxShadow: '0 8px 22px rgba(0,0,0,0.05)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        height: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) 32px',
                          gap: 0.75,
                          alignItems: 'center',
                          height: '100%',
                          minHeight: 62,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            title={card.title}
                            sx={{
                              display: 'block',
                              fontWeight: 700,
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {card.title}
                          </Typography>
                          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.1, mt: 0.25 }}>
                            {card.value}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            title={card.description}
                            sx={{
                              display: 'block',
                              lineHeight: 1.2,
                              mt: 0.25,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {card.description}
                          </Typography>
                        </Box>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: `${card.color}18`,
                            color: card.color,
                            justifySelf: 'end',
                            '& .MuiSvgIcon-root': {
                              fontSize: 19,
                            },
                          }}
                        >
                          <IconComponent />
                        </Avatar>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>

              <SectionTable
                title="General Caches"
                description="Shared caches that are not tied to a reporting date window."
                columns={[
                  { key: 'name', label: 'Cache name' },
                  { key: 'size', label: 'Size', align: 'right' },
                ]}
                rows={generalCacheRows}
                emptyMessage="No general caches are populated yet."
              />

              <SectionTable
                title="Article Stats Caches"
                description="Precomputed article-statistics ranges ordered by their start date."
                columns={dateWindowColumns}
                rows={sortedArticleStats}
                emptyMessage="No article statistics caches are available."
              />

              <SectionTable
                title="Customer Stats Caches"
                description="Precomputed customer-statistics ranges ordered by their start date."
                columns={dateWindowColumns}
                rows={sortedCustomerStats}
                emptyMessage="No customer statistics caches are available."
              />

              <SectionTable
                title="Order Caches"
                description="Precomputed order ranges ordered by their start date."
                columns={dateWindowColumns}
                rows={sortedOrders}
                emptyMessage="No order caches are available."
              />

              <SectionTable
                title="Top Spender Caches"
                description="Precomputed top-spender ranges ordered by their start date."
                columns={dateWindowColumns}
                rows={sortedTopSpenders}
                emptyMessage="No top-spender caches are available."
              />

              <SectionTable
                title="Top Seller Caches"
                description="Precomputed top-seller ranges ordered by their start date."
                columns={dateWindowColumns}
                rows={sortedTopSellers}
                emptyMessage="No top-seller caches are available."
              />
            </Stack>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
